import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { Dolan } from '@sapappgyver/appgyver-descriptors/lib/dolan';
import {
  ComponentDescription,
  ComponentInstance,
  DolanFonts,
  StyleBinding,
  StyleBindings,
  StyleClassDefinitionsByComponentId,
  ThemeVariables,
} from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { CommonBinding } from '@sapappgyver/appgyver-descriptors/lib/common/bindings';
import { createSelector, weakMapMemoize } from '@reduxjs/toolkit';
import getKnownFontVariantsByFamily from './getKnownFontVariantsByFamily';
import { getBindingKey, getBindingSource } from '../../modules/bindings';
import mergeCached from '../../utilities/mergeCached';

interface UsedFontVariant {
  fontFamily: string;
  fontStyle: string;
  fontWeight: string;
}

function resolvePossibleStaticStringValues(
  binding: StyleBinding | CommonBinding | null | undefined,
  theme: ThemeVariables = {},
): string[] {
  if (binding == null) {
    return [];
  }
  if (typeof binding === 'string') {
    return [binding];
  }
  if (typeof binding !== 'object') {
    return [];
  }
  const source = getBindingSource(binding);
  const key = getBindingKey(binding);
  if (source === 'str' && typeof key === 'string') {
    return [key];
  }
  if (source === 'theme' && typeof key === 'string') {
    const variable = theme[key];
    const varBinding = ((variable && variable.binding) || variable) as StyleBinding | CommonBinding;
    // Prevent infinite recursion
    const recursionTheme = { ...theme };
    delete recursionTheme[key];
    return varBinding == null ? [] : resolvePossibleStaticStringValues(varBinding, recursionTheme);
  }
  return [];
}

const getComponentStructureFontStyles = weakMapMemoize((component: ComponentInstance): StyleBindings[] => {
  // Find font styles for this component
  const { style, nested_overrides } = component;
  const styles: StyleBindings[] = [];
  if (style.fontFamily) {
    styles.push(style);
  }
  if (nested_overrides) {
    Object.values(nested_overrides).forEach((override) => {
      const { style: overrideStyle } = override;
      if (overrideStyle?.fontFamily) {
        styles.push(overrideStyle);
      }
    });
  }
  // Recursively fint font styles from children
  component.children.forEach((child) => {
    const childStyles = getComponentStructureFontStyles(child);
    styles.push(...childStyles);
  });
  return styles;
});

type View = Dolan['views'][number];
const getViewFontStyles = weakMapMemoize((views: View[]): StyleBindings[] =>
  views.flatMap((view) => getComponentStructureFontStyles(view.layout.root_component)),
);

const getComponentDescriptionDefaultFontStyles = weakMapMemoize((descriptions: ComponentDescription[]) => {
  const defaultFontStyles: StyleBindings[] = [];
  descriptions.forEach((description) => {
    const styleInfo = description?.reactnative_style_properties;
    const defaultStyle = styleInfo?.default_values;
    if (defaultStyle?.fontFamily) {
      defaultFontStyles.push(defaultStyle);
    }
  });
  return defaultFontStyles;
});

function pushUniqueUsedFontVariant(
  usedFontVariants: UsedFontVariant[],
  fontFamily: string,
  fontWeight: string,
  fontStyle: string,
) {
  const newVariant: UsedFontVariant = {
    fontFamily,
    fontStyle: fontStyle || 'normal',
    fontWeight: fontWeight || '400',
  };
  if (usedFontVariants.every((variant) => !isEqual(variant, newVariant))) {
    usedFontVariants.push(newVariant);
  }
}

const getUsedFontVariantsFromTheme = weakMapMemoize(
  (theme: ThemeVariables, fonts: DolanFonts | undefined): UsedFontVariant[] => {
    const usedFontVariants: UsedFontVariant[] = [];
    Object.values(theme).forEach((themeVariable) => {
      if (themeVariable.role === 'fontFamily') {
        const fontFamilies = resolvePossibleStaticStringValues(themeVariable.binding, theme);
        fontFamilies.forEach((fontFamily) => {
          const fontVariants = getKnownFontVariantsByFamily(fontFamily, fonts);
          fontVariants.forEach((fontVariant) => {
            pushUniqueUsedFontVariant(usedFontVariants, fontVariant.font_family, fontVariant.weight, fontVariant.style);
          });
        });
      }
    });
    return usedFontVariants;
  },
);

const getStyleClassFontStyles = weakMapMemoize(
  (styleClassDefsById: StyleClassDefinitionsByComponentId): StyleBindings[] => {
    const styles: StyleBindings[] = [];
    Object.values(styleClassDefsById).forEach((styleClasses) => {
      Object.values(styleClasses).forEach(({ nested_overrides, style }) => {
        if (nested_overrides) {
          Object.values(nested_overrides).forEach((override) => {
            const { style: overrideStyle } = override;
            if (overrideStyle?.fontFamily) {
              styles.push(overrideStyle);
            }
          });
        }
        if (style?.fontFamily) {
          styles.push(style);
        }
      });
    });
    return styles;
  },
);

/**
 * Traverses through the component hierarchies in DOLAN and returns list of all font
 * variants that are certainly used in the app. The returned array contains
 * objects with `fontFamily`, `fontWeight` and `fontStyle` properties.
 */
const getAllUsedFontVariantsInDolan = createSelector(
  (dolan: Dolan) => dolan.components,
  (dolan: Dolan) => mergeCached(dolan.theme_meta?.theme, dolan.theme),
  (dolan: Dolan) => mergeCached(dolan.theme_meta?.style_classes, dolan.style_classes),
  (dolan: Dolan) => dolan.views,
  (dolan: Dolan) => dolan.fonts,
  (descriptions, theme, styleClassDefsById, views, fonts): UsedFontVariant[] => {
    const usedFontVariants: UsedFontVariant[] = [];

    function pushUsedFontVariantsFromBinding(
      fontFamilyBinding?: StyleBinding | null,
      fontWeightBinding?: StyleBinding | null,
      fontStyleBinding?: StyleBinding | null,
    ) {
      const bindingFontFamilies = resolvePossibleStaticStringValues(fontFamilyBinding, theme);
      const bindingFontWeights = resolvePossibleStaticStringValues(fontWeightBinding, theme);
      const bindingFontStyles = resolvePossibleStaticStringValues(fontStyleBinding, theme);
      bindingFontFamilies.forEach((fontFamily) => {
        if (!fontFamily) {
          // Ignore "unset" font family (system default)
          return;
        }
        const fontVariants = getKnownFontVariantsByFamily(fontFamily, fonts);
        // If could not resolve any font weight then assume all known weights for the font
        const fontWeights = bindingFontWeights.length
          ? bindingFontWeights
          : uniq(fontVariants.map((variant) => variant.weight));
        // If could not resolve any font style then assume all known weights for the font
        const fontStyles = bindingFontStyles.length
          ? bindingFontStyles
          : uniq(fontVariants.map((variant) => variant.style));
        fontWeights.forEach((fontWeight) => {
          fontStyles.forEach((fontStyle) => {
            pushUniqueUsedFontVariant(usedFontVariants, fontFamily, fontWeight, fontStyle);
          });
        });
      });
    }

    // Include usage in all components in all views
    getViewFontStyles(views).forEach((style) => {
      pushUsedFontVariantsFromBinding(style.fontFamily, style.fontWeight, style.fontStyle);
    });
    // Include usage in component description default styles
    getComponentDescriptionDefaultFontStyles(descriptions).forEach((style) => {
      pushUsedFontVariantsFromBinding(style.fontFamily, style.fontWeight, style.fontStyle);
    });
    // Include usage in theme variables
    getUsedFontVariantsFromTheme(theme, fonts).forEach((fontVariant) => {
      pushUniqueUsedFontVariant(
        usedFontVariants,
        fontVariant.fontFamily,
        fontVariant.fontWeight,
        fontVariant.fontStyle,
      );
    });
    // Include usage in style classes
    getStyleClassFontStyles(styleClassDefsById).forEach((style) => {
      pushUsedFontVariantsFromBinding(style.fontFamily, style.fontWeight, style.fontStyle);
    });
    return usedFontVariants;
  },
);

export default getAllUsedFontVariantsInDolan;
